import _ from 'lodash'

import { usePatents } from 'src/patents/PatentsProvider'
import { Instruction, RenewalState } from 'src/renewal/interfaces/renewal_types'
import { useDennemeyer, useIpRight, useMaintenances, useTimeline } from '../DennemeyerProvider'
import { RenewalOverviewContext, RenewalsContext } from 'src/renewal/interfaces/Renwals'
import { importStatusExtraction, renewalNoticesStatus } from './modern_states'
import { dmIpRightToIpRight, getOwnerAndCostCenters, maintenanceActionToRenewal, toCommonInstructionType, useInstruct } from './utils'
import { useEffect } from 'react'
import { eliminateDoubleRenewals } from 'src/renewal/utils/renewals'

export function DennemeyerRenewalOverviewProvider({ children, familyMemberId }: { familyMemberId: number; children: React.ReactNode }) {
  const { memberById } = usePatents()
  const member = memberById[familyMemberId]
  const { instructionByDennemeyerId, ipRightByMemberId: pcIpRightByMemberId, upIpRightByMemberId, ownershipsByIpRight, costCentersByIpRightId, isLoading } = useDennemeyer()
  const {calculateDueDates} = useTimeline()

  const pcIpRight = pcIpRightByMemberId[familyMemberId]

  const { ipRight: ipRightData, isLoading: isIpRightLoading } = useIpRight(pcIpRight?.dennemeyerId)
  const dmIpRight = ipRightData?.Data
  const standardMaintenanceActions = dmIpRight?.MaintenanceActions ?? []

  const upIpRight = upIpRightByMemberId[familyMemberId]
  const { ipRight: upIpRightData } = useIpRight(upIpRight?.dennemeyerId)
  const dmUpIpRight = upIpRightData?.Data
  const upMaintenanceActions = dmUpIpRight?.MaintenanceActions ?? []

  const instructions = [...standardMaintenanceActions, ...upMaintenanceActions].map(ma => instructionByDennemeyerId[ma.DennemeyerId]).filter(Boolean)

  const instructionByRenewalId: Record<string, Instruction> = _(instructions)
    .map(i => ({ ...i, instruction: toCommonInstructionType(i.instruction) }))
    .filter(i => i.instruction !== undefined)
    .keyBy(i => i.dennemeyerId)
    .value()

  const standardRenewals = _(standardMaintenanceActions)
    .map(maintenanceAction => maintenanceActionToRenewal(maintenanceAction, calculateDueDates, instructionByRenewalId, pcIpRight?.ipRightId + ''))
    .value()
  const upRenewals = _(upMaintenanceActions)
    .map(maintenanceAction => maintenanceActionToRenewal(maintenanceAction, calculateDueDates, instructionByRenewalId, upIpRight?.ipRightId + ''))
    .value()

  const renewals = [...standardRenewals, ...upRenewals]

  // console.log({ renewals, actions: dmIpRight?.MaintenanceActions, pcIpRight, familyMemberId })
  const renewalById = _.keyBy(renewals, r => r.id)
  const {instruct} = useInstruct({renewalById})

  if (!member) {
    return null
  }

  const ourIpRight = upIpRight ?? pcIpRight
  if (!ourIpRight) {
    // return children
    const ipRightImportStatus = importStatusExtraction({ member, pcIpRight: undefined, ipRight: undefined })
    const renewalsStatus = renewalNoticesStatus({ renewalStatus: ipRightImportStatus.status, renewals, maintenanceActions: []})
    const value = {
      member,
      ipRight: undefined,
      actualIpRight: undefined,
      renewals,
      instruct,
      ipRightImportStatus, renewalsStatus,
      isLoading: false,
    }
    return <RenewalOverviewContext.Provider {...{ value }}>{children}</RenewalOverviewContext.Provider>
  }

  const { owner, costCenters } = getOwnerAndCostCenters(ourIpRight, ownershipsByIpRight, costCentersByIpRightId)
  const ipRight = dmIpRightToIpRight(ourIpRight, member, owner, costCenters)

  // console.log({ ipRight, renewals})
  const renewalsForIpRight = _.values(eliminateDoubleRenewals(renewals))

  const maintenanceActions = [...standardMaintenanceActions, ...upMaintenanceActions].filter(ma => renewalsForIpRight.some(r => r.id === ma.DennemeyerId))

  // console.log({ ipRight, familyMemberId, ipRightByFamilyMemberId, renewals, allRenewals })
  const ipRightImportStatus = importStatusExtraction({ member, pcIpRight: ourIpRight, ipRight: dmUpIpRight ?? dmIpRight })
  const renewalsStatus = renewalNoticesStatus({ renewalStatus: ipRightImportStatus.status, renewals, maintenanceActions})

  const value = {
    member,
    ipRight,
    actualIpRight: ourIpRight,
    renewals: renewalsForIpRight,
    instruct,
    ipRightImportStatus, renewalsStatus,
    isLoading: isLoading || isIpRightLoading,
  }

  return <RenewalOverviewContext.Provider {...{ value }}>{children}</RenewalOverviewContext.Provider>
}

export function DennemeyerRenewalsProvider({
  start,
  end,
  states,
  children,
}: {
  start?: string
  end?: string
  states: RenewalState[]
  children: React.ReactNode
}) {
  const { firstPossibleFeeDueDate, latestPossibleFeeDueDate, calculateDueDates } = useTimeline()
  const customRange = start !== undefined && end !== undefined
  
  const onlyOpen = states.length === 1 && states[0] === 'Open'
  const { data, fetchNextPage, hasNextPage, isLoading } = useMaintenances({
    onlyOpen,
    onlyInstructable: false,
    ...(customRange ? { minDate: firstPossibleFeeDueDate(start), maxDate: latestPossibleFeeDueDate(end) } : {}),
  })
  useEffect(() => {
    if (hasNextPage && !isLoading) fetchNextPage()
  }, [fetchNextPage, hasNextPage, isLoading])

  const { instructions } = useDennemeyer()

  const instructionByRenewalId: Record<string, Instruction> = _(instructions)
    .map(i => ({ ...i, instruction: toCommonInstructionType(i.instruction) }))
    .filter(i => i.instruction !== undefined)
    .keyBy(i => i.dennemeyerId)
    .value()

  const renewals = _(data?.pages ?? [])
    .flatMap(page => page?.Data?.Page ?? [])
    .map(maintenanceAction => 
      maintenanceActionToRenewal(maintenanceAction, calculateDueDates, instructionByRenewalId)
    )
    .filter(r => states.includes(r.state))
    .value()

  const renewalById = _.keyBy(renewals, r => r.id)

  const {instruct, bulkInstruct} = useInstruct({renewalById})

  const value = {
    renewals,
    instruct,
    bulkInstruct,
    isLoading,
  }

  return <RenewalsContext.Provider {...{ value }}>{children}</RenewalsContext.Provider>
}